import { useState } from "react";
import { X, Loader2, AlertTriangle, ShieldAlert, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
    requestAdminCompanyDeleteOtp,
    deleteAdminCompany,
} from "@/lib/api";

/**
 * DeleteCompanyDialog — 3-step permanent deletion of a tenant.
 *  1. Warning + type company name to unlock
 *  2. Request OTP (sent to ADMIN_2FA_EMAIL)
 *  3. Enter 6-digit OTP + confirm
 *
 * After success, the tenant is completely wiped (data + login + integrations).
 * The `admin_audit` trail retains the event for compliance.
 */
export default function DeleteCompanyDialog({ open, company, onClose, onDeleted }) {
    const [step, setStep] = useState(1);
    const [typedName, setTypedName] = useState("");
    const [code, setCode] = useState("");
    const [otpDeliveredTo, setOtpDeliveredTo] = useState([]);
    const [otpDevCode, setOtpDevCode] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    if (!open || !company) return null;

    const canonicalName = (company.name || "").trim();
    const nameMatches = typedName.trim().toLowerCase() === canonicalName.toLowerCase() && canonicalName.length > 0;
    const codeValid = /^\d{6}$/.test(code.trim());

    const handleClose = () => {
        setStep(1); setTypedName(""); setCode(""); setOtpDeliveredTo([]); setOtpDevCode(null);
        onClose?.();
    };

    const requestOtp = async () => {
        setRequesting(true);
        try {
            const r = await requestAdminCompanyDeleteOtp(company.id);
            setOtpDeliveredTo(r.sent_to || []);
            setOtpDevCode(r.dev_code || null);
            setStep(3);
            toast.success("Slette-kode sendt til admin-mailen");
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Kunne ikke sende kode";
            toast.error(msg);
        } finally {
            setRequesting(false);
        }
    };

    const doDelete = async () => {
        setDeleting(true);
        try {
            const r = await deleteAdminCompany(company.id, { code: code.trim(), confirm_name: typedName.trim() });
            toast.success(`Virksomhed permanent slettet · ${sumCounts(r.deleted)} dokumenter fjernet`);
            onDeleted?.(r);
            handleClose();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Kunne ikke slette virksomhed";
            toast.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kunde-delete-dialog">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border-2 border-rose-200">
                <div className="px-6 py-4 border-b border-rose-100 bg-rose-50/60 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-rose-600" />
                        <h2 className="text-base font-bold text-rose-900">Slet virksomhed permanent</h2>
                    </div>
                    <button onClick={handleClose} className="p-1 hover:bg-rose-100 rounded" data-testid="kunde-delete-close">
                        <X className="w-4 h-4 text-rose-500" />
                    </button>
                </div>

                {/* Step 1 — warning + name confirm */}
                {step === 1 && (
                    <div className="p-6 space-y-4">
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                            <div className="text-sm text-rose-900 space-y-1">
                                <p className="font-semibold">Dette kan ikke fortrydes.</p>
                                <p className="text-xs text-rose-800">
                                    Alle data for <strong>{canonicalName}</strong> slettes permanent — jobs, kunder, medarbejdere, materialer, fakturaer, integrationer, brugere og logins.
                                    Det vil være som om virksomheden aldrig har været en del af MyServiceHelper.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                Bekræft ved at skrive virksomhedens navn nedenfor:
                            </label>
                            <div className="mt-1 mb-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-700 select-all">
                                {canonicalName}
                            </div>
                            <input
                                type="text"
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                                placeholder="Skriv navnet præcis som ovenfor…"
                                data-testid="kunde-delete-name-input"
                                autoComplete="off"
                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 text-sm outline-none"
                            />
                            {typedName.length > 0 && !nameMatches && (
                                <p className="text-xs text-rose-600 mt-1">Navnet matcher ikke.</p>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                                data-testid="kunde-delete-cancel-step1"
                            >
                                Annullér
                            </button>
                            <button
                                onClick={() => setStep(2)}
                                disabled={!nameMatches}
                                data-testid="kunde-delete-next-step2"
                                className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-xl"
                            >
                                Fortsæt →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2 — request OTP */}
                {step === 2 && (
                    <div className="p-6 space-y-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                            <Mail className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-900 space-y-1">
                                <p className="font-semibold">Vi sender en 6-cifret kode til admin-mailen.</p>
                                <p className="text-xs text-amber-800">
                                    Koden udløber om 10 minutter. Kun den admin, der modtager mailen, kan bekræfte sletningen.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <button
                                onClick={() => setStep(1)}
                                data-testid="kunde-delete-back-step1"
                                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
                            >
                                ← Tilbage
                            </button>
                            <button
                                onClick={requestOtp}
                                disabled={requesting}
                                data-testid="kunde-delete-request-otp"
                                className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-xl"
                            >
                                {requesting && <Loader2 className="w-4 h-4 animate-spin" />}
                                <Mail className="w-4 h-4" /> Send slette-kode
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3 — enter OTP + delete */}
                {step === 3 && (
                    <div className="p-6 space-y-4">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600">
                            Kode sendt til: <strong>{otpDeliveredTo.join(", ") || "admin-mailen"}</strong>
                            {otpDevCode && (
                                <p className="mt-1 text-amber-700"><strong>Dev-kode (Resend ikke sat):</strong> {otpDevCode}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                6-cifret slette-kode
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                placeholder="000000"
                                data-testid="kunde-delete-otp-input"
                                autoComplete="off"
                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 font-mono text-lg tracking-[0.4em] text-center outline-none"
                            />
                        </div>
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2 text-xs text-rose-800">
                            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                            Når du klikker <strong>Slet permanent</strong>, forsvinder <strong>{canonicalName}</strong> og al tilknyttet data uigenkaldeligt.
                        </div>
                        <div className="flex justify-between items-center pt-1">
                            <button
                                onClick={() => setStep(2)}
                                data-testid="kunde-delete-back-step2"
                                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
                            >
                                ← Tilbage
                            </button>
                            <div className="flex gap-2">
                                <button
                                    onClick={requestOtp}
                                    disabled={requesting}
                                    className="text-xs text-slate-500 hover:text-slate-700 px-3 py-2"
                                    data-testid="kunde-delete-resend"
                                >
                                    Send ny kode
                                </button>
                                <button
                                    onClick={doDelete}
                                    disabled={!codeValid || deleting}
                                    data-testid="kunde-delete-confirm"
                                    className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2 rounded-xl"
                                >
                                    {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <Trash2 className="w-4 h-4" /> Slet permanent
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function sumCounts(counts) {
    if (!counts) return 0;
    return Object.values(counts).reduce((s, v) => s + (typeof v === "number" && v > 0 ? v : 0), 0);
}
